import Link from "next/link";
import { useRouter } from "next/router";

import React, { useCallback } from "react";

import { Entity } from "models/entity";
import { twMerge } from "tailwind-merge";

import EntityPrimerButton from "components/DashboardPage/DashboardPageTabs/EntityPrimerButton";
import useAuthentication from "hooks/useAuthentication";

import EntityTopInvestorQuestionButton from "../DashboardPageTabs/EntityTopInvestorQuestionButton";

type AIWorkflowWidgetProps = {
  entity?: Entity;
  onRequestPrimer?: (opts?: { userInitiated?: boolean }) => void;
};

const AIWorkflowWidget = ({
  entity,
  onRequestPrimer: onRequestPrimerProp,
}: AIWorkflowWidgetProps) => {
  const router = useRouter();
  const { account: loggedInAccount } = useAuthentication();

  const handleRequestPrimer = useCallback(
    (opts?: { userInitiated?: boolean }) => {
      onRequestPrimerProp?.(opts);
    },
    [onRequestPrimerProp]
  );

  const openTopInvestorQuestionsModal = useCallback(() => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          "top-investor-questions": "true",
        },
      },
      undefined,
      { shallow: true, scroll: false }
    );
  }, [router]);

  return (
    <>
      <div
        className={twMerge(
          "mx-[15px] my-[16px]",
          "rounded-xl",
          "border border-solid border-inactive-gray",
          "bg-white",
          "p-[20px]",
          "shadow-sm"
        )}
      >
        {/* Title */}
        <div className="flex items-center gap-[6px] mb-[12px]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://assets.smartkarma.com/icons/sparkle.svg"
            alt="AI"
            width={14}
            height={14}
          />
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
            AI Workflows
          </span>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap gap-[8px] items-center">
          {loggedInAccount?.canRequestAIPrimer && (
            <div>
              {entity ? (
                <button
                  onClick={() => handleRequestPrimer({ userInitiated: true })}
                  className={twMerge(
                    "py-[8px] px-[16px] rounded-full border-none cursor-pointer",
                    "text-[14px] font-normal text-[#2a9d78] leading-snug",
                    "bg-[#e6f4f0] hover:bg-[#d0ece5] transition-colors"
                  )}
                >
                  + Primer
                </button>
              ) : (
                <EntityPrimerButton className="!text-[14px] leading-snug !px-[16px] !py-[8px] !rounded-full !bg-[#e6f4f0] !text-[#2a9d78] hover:!bg-[#d0ece5]" />
              )}
            </div>
          )}

          <div>
            <Link
              href={`/tools/comparables${
                entity ? `?entity-id=${entity?.id}` : ""
              }`}
              className={twMerge(
                "block py-[8px] px-[16px] rounded-full no-underline",
                "text-[14px] font-normal text-[#2a9d78] leading-snug",
                "bg-[#e6f4f0] hover:bg-[#d0ece5] transition-colors"
              )}
            >
              + Comparables
            </Link>
          </div>

          {loggedInAccount?.canRequestAIPrimer && (
            <div>
              {entity ? (
                <button
                  type="button"
                  onClick={openTopInvestorQuestionsModal}
                  className={twMerge(
                    "block py-[8px] px-[16px] border-none rounded-full no-underline",
                    "text-[14px] font-normal text-[#2a9d78] leading-snug",
                    "bg-[#e6f4f0] hover:bg-[#d0ece5] transition-colors"
                  )}
                >
                  + Meeting Prep
                </button>
              ) : (
                <EntityTopInvestorQuestionButton
                  className={twMerge(
                    "block py-[8px] px-[16px] rounded-full no-underline",
                    "text-[14px] font-normal text-[#2a9d78] leading-snug",
                    "bg-[#e6f4f0] hover:bg-[#d0ece5] transition-colors"
                  )}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AIWorkflowWidget;
